import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Stack, styled } from "@mui/material";
import { useMemo } from "react";
import { getProductHandoutType } from "src/api/product";
import useHandoutProductStore from "src/store/relatedProduct";
import PurchaseAlreadyOwnedTab from "./PurchaseAlreadyOwnedTab";
import PurchaseFreePassTab from "./PurchaseFreePassTab";
import PurchaseFreeTrialTab from "./PurchaseFreeTrialTab";
import PurchaseModals from "./PurchaseModals";
import PurchaseNoFreepassTab from "./PurchaseNoFreepassTab";
import PurchaseTabs from "./PurchaseTabs";
import PurchaseTrialOverTab from "./PurchaseTrialOverTab";
import PurchaseUnavailableTab from "./PurchaseUnavailbleTab";
import PurchaseValidMembershipTab from "./PurchaseValidMembershipTab";
import useEntitlementsQuery from "./useEntitlementsQuery";
const PurchaseRoot = styled(Stack)((param)=>{
    let { theme  } = param;
    return {
        maxHeight: "calc(100vh - 219px)",
        backgroundColor: theme.palette.common.white,
        borderRadius: "12px",
        boxShadow: "0px 2px 8px 0px rgba(0, 0, 0, 0.08), 0px 1px 4px 0px rgba(0, 0, 0, 0.04)",
        padding: theme.spacing(2.5),
        width: "100%",
        border: "1px solid ".concat(theme.palette.grey[50])
    };
});
/** 무료체험 가능 여부 확인 (Case 1) */ const isEligibleForFreeTrial = (freeTrialData)=>{
    if (!freeTrialData) return false;
    return freeTrialData.isEligible || freeTrialData.reason === "not_logged_in";
};
/** 무료체험 끝 → 구매 유도 대상인지 확인 (Case 2) */ const isTrialOver = (freeTrialData)=>{
    if (!freeTrialData) return false;
    return freeTrialData.reason === "daily_limit_exceeded";
};
/** 무료획득으로 보유 → 사용 유도 대상인지 확인 (Case 3) */ const isAlreadyOwned = (freeTrialData)=>{
    if (!freeTrialData) return false;
    return freeTrialData.reason === "already_owned" && freeTrialData.isFreeOwned === true;
};
/** 유효한 이용권 보유 → 사용 유도 대상인지 확인 (Case 4) */ const hasValidMembership = (freeTrialData)=>{
    if (!freeTrialData) return false;
    return freeTrialData.reason === "excluded_membership";
};
const getPurchaseState = (product, isLoggedIn, hasUsableFreepass, freeTrialData, hasUsableMembership)=>{
    if (!product.available) return "unavailable";
    // Case 1: 무료체험 가능한 경우
    if (isEligibleForFreeTrial(freeTrialData)) {
        // Case 1-1: 이용 가능한 이용권이 있으면 이용권 선택 UI 표시
        if (hasUsableMembership) {
            return "hasValidMembership";
        }
        // Case 1-2: 이용권이 없으면 무료로 받기
        return "freeTrial";
    }
    // Case 2: 무료체험 끝 → 구매 유도
    if (isTrialOver(freeTrialData)) {
        return "trialOver";
    }
    // Case 3: 무료체험으로 보유 → 사용 유도
    if (isAlreadyOwned(freeTrialData)) {
        return "alreadyOwned";
    }
    // Case 4: 유효한 이용권 보유 → 사용 유도
    if (hasValidMembership(freeTrialData)) {
        return "hasValidMembership";
    }
    const source = product.source;
    if ((source === null || source === void 0 ? void 0 : source.type) === "freepass") {
        if (!hasUsableFreepass || !isLoggedIn) return "noFreepass";
        return "freepass";
    }
    return "default";
};
/** 스켈레톤 컴포넌트 */ const PurchaseSkeleton = ()=>/*#__PURE__*/ _jsxs("div", {
        className: "flex animate-pulse flex-col gap-4",
        children: [
            /*#__PURE__*/ _jsx("div", {
                className: "h-12 rounded-lg bg-gray-100"
            }),
            /*#__PURE__*/ _jsx("div", {
                className: "h-10 rounded-lg bg-gray-100"
            })
        ]
    });
const PurchaseSection = (param)=>{
    let { sx , product , hasUsableFreepass , isLoggedIn , fullWidth , freeTrialData , isFreeTrialLoading , isInModal  } = param;
    const { selectedPurchaseOption  } = useHandoutProductStore((state)=>({
            selectedPurchaseOption: state.selectedPurchaseOption
        }));
    // 이용 가능한 이용권 확인 (무료체험 대상자의 이용권 보유 여부 체크용)
    const handoutType = getProductHandoutType(product);
    const { data: passes = [] , isSuccess: isMembershipLoaded  } = useEntitlementsQuery({
        productId: product.id,
        isLoggedIn,
        freepassOnly: handoutType === "freepass",
        selectedPurchaseOption,
        productHandoutType: handoutType
    });
    const hasUsableMembership = isMembershipLoaded && passes.some((pass)=>!pass.isDisabled);
    const purchaseContent = useMemo(()=>{
        // 로딩 중일 때 스켈레톤 표시 (로그인 상태에서 membership 데이터가 아직 로드되지 않았으면 대기)
        if (isFreeTrialLoading || isLoggedIn && !isMembershipLoaded) {
            return /*#__PURE__*/ _jsx(PurchaseSkeleton, {});
        }
        const purchaseState = getPurchaseState(product, isLoggedIn, hasUsableFreepass, freeTrialData, hasUsableMembership);
        switch(purchaseState){
            case "freeTrial":
                return /*#__PURE__*/ _jsx(PurchaseFreeTrialTab, {
                    product: product,
                    isLoggedIn: isLoggedIn,
                    freeTrialData: freeTrialData
                });
            case "trialOver":
                return /*#__PURE__*/ _jsx(PurchaseTrialOverTab, {
                    product: product,
                    freeTrialData: freeTrialData,
                    fullWidth: fullWidth,
                    isInModal: isInModal
                });
            case "alreadyOwned":
                return /*#__PURE__*/ _jsx(PurchaseAlreadyOwnedTab, {
                    product: product,
                    freeTrialData: freeTrialData,
                    isInModal: isInModal
                });
            case "hasValidMembership":
                return /*#__PURE__*/ _jsx(PurchaseValidMembershipTab, {
                    product: product,
                    isInModal: isInModal
                });
            case "default":
                return /*#__PURE__*/ _jsx(PurchaseTabs, {
                    isLoggedIn: isLoggedIn,
                    product: product,
                    fullWidth: fullWidth
                });
            case "freepass":
                return /*#__PURE__*/ _jsx(PurchaseFreePassTab, {
                    isLoggedIn: isLoggedIn,
                    product: product,
                    fullWidth: fullWidth
                });
            case "noFreepass":
                return /*#__PURE__*/ _jsx(PurchaseNoFreepassTab, {
                    product: product
                });
            case "unavailable":
                return /*#__PURE__*/ _jsx(PurchaseUnavailableTab, {});
            default:
                return null;
        }
    }, [
        product,
        hasUsableFreepass,
        isLoggedIn,
        fullWidth,
        freeTrialData,
        isFreeTrialLoading,
        isInModal,
        hasUsableMembership,
        isMembershipLoaded
    ]);
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(PurchaseRoot, {
                sx: sx,
                children: purchaseContent
            }),
            /*#__PURE__*/ _jsx(PurchaseModals, {
                product: product
            })
        ]
    });
};
export default PurchaseSection;
